import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import {
  Card,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Spin,
  Breadcrumb,
  Divider,
  Tooltip,
  Input
} from 'antd';
import {
  SaveOutlined,
  RollbackOutlined,
  UserOutlined,
  HomeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';

import { UserService } from '../../../services/userService';
import type { User, Program, UserPermission } from '../../../types/user';
import { PERMISSION_ACTIONS } from '../../../types/user';

const { Title, Text } = Typography;

interface PersonalPermissionEditState {
  user: User | null;
  programs: Program[];
  permissions: UserPermission[];
  loading: boolean;
  saving: boolean;
}

const PersonalPermissionEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();

  const [searchText, setSearchText] = useState('');

  const [state, setState] = useState<PersonalPermissionEditState>({
    user: null,
    programs: [],
    permissions: [],
    loading: true,
    saving: false,
  });

  useEffect(() => {
    if (userId) {
      loadData(userId);
    }
  }, [userId]);

  const loadData = async (userId: string) => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      // 同時載入使用者資訊、程式清單和權限
      const [user, programs, permissions] = await Promise.all([
        UserService.getUser(userId),
        UserService.listPrograms(),
        UserService.getUserPermissions(userId)
      ]);

      // 建立完整的權限列表
      const existingPermissions = permissions || [];
      const fullPermissions = programs.map(program => {
        const existing = existingPermissions.find((p: any) => p.programId === program.pgId);
        if (existing) {
          return existing;
        }
        // 如果沒有現有權限，建立預設權限（全部為 null = 未設定/繼承群組）
        return {
          programId: program.pgId,
          programName: program.pgName,
          query: null,
          addNew: null,
          edit: null,
          delete: null,
          export: null,
          print: null,
          approve: null
        };
      });

      setState({
        user,
        programs,
        permissions: fullPermissions,
        loading: false,
        saving: false,
      });
    } catch (error) {
      message.error('載入資料失敗');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // 三態循環：null(未設定) → true(開) → false(關) → null(未設定)
  const cyclePermission = (current: boolean | null): boolean | null => {
    if (current === null) return true;
    if (current === true) return false;
    return null;
  };

  const handlePermissionChange = (programId: number, permissionType: keyof UserPermission) => {
    setState(prev => {
      const newPermissions = [...prev.permissions];
      const existingIndex = newPermissions.findIndex(p => p.programId === programId);

      if (existingIndex !== -1) {
        const currentValue = newPermissions[existingIndex][permissionType] as boolean | null;
        newPermissions[existingIndex] = {
          ...newPermissions[existingIndex],
          [permissionType]: cyclePermission(currentValue)
        };
      } else {
        const program = state.programs.find(p => p.pgId === programId);
        if (program) {
          const newPermission: UserPermission = {
            programId,
            programName: program.pgName,
            query: null,
            addNew: null,
            edit: null,
            delete: null,
            export: null,
            print: null,
            approve: null,
            [permissionType]: true
          };
          newPermissions.push(newPermission);
        }
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  const handleSave = async () => {
    if (!userId) return;
    
    try {
      setState(prev => ({ ...prev, saving: true }));
      
      // 只儲存有任何明確設定（非全 null）的項目
      const permissionsToSave = state.permissions.filter(p =>
        p.query !== null || p.addNew !== null || p.edit !== null ||
        p.delete !== null || p.export !== null || p.print !== null || p.approve !== null
      );
      
      await UserService.setUserPermissions(userId, { 
        permissions: permissionsToSave 
      });
      
      message.success('個人權限更新成功');
      await refreshPermissions();
      navigate('/user/personal-permissions');
    } catch (error) {
      message.error(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleCancel = () => {
    navigate('/user/personal-permissions');
  };

  const renderProgramPermissions = (program: Program, level: number = 0) => {
    const permission = state.permissions.find(p => p.programId === program.pgId);
    
    return (
      <React.Fragment key={program.pgId}>
        <div style={{ 
          marginBottom: '12px',
          paddingLeft: `${level * 24}px`
        }}>
          <Row align="middle" gutter={[16, 0]}>
            <Col span={8}>
              <Text strong style={{ fontSize: '14px' }}>
                {level > 0 && '└─ '}{program.pgName}
              </Text>
            </Col>
            <Col span={16}>
              <Space size={[8, 8]} wrap>
                {Object.entries(PERMISSION_ACTIONS).map(([key, action]) => {
                  const value = (permission?.[action as keyof UserPermission] as boolean | null) ?? null;

                  let icon: React.ReactNode;
                  let color: string;
                  let tooltip: string;
                  if (value === null) {
                    icon = <MinusCircleOutlined />;
                    color = '#d9d9d9';
                    tooltip = '未設定（繼承群組）';
                  } else if (value === true) {
                    icon = <CheckCircleFilled />;
                    color = '#52c41a';
                    tooltip = '明確允許';
                  } else {
                    icon = <CloseCircleFilled />;
                    color = '#ff4d4f';
                    tooltip = '明確禁止（覆蓋群組）';
                  }

                  return (
                    <Tooltip key={action} title={tooltip}>
                      <Button
                        type="text"
                        size="small"
                        icon={icon}
                        style={{ color, minWidth: 80 }}
                        onClick={() => handlePermissionChange(program.pgId, action as keyof UserPermission)}
                      >
                        {key.replace('_', '')}
                      </Button>
                    </Tooltip>
                  );
                })}
              </Space>
            </Col>
          </Row>
        </div>
        {program.children && program.children.map(child => renderProgramPermissions(child, level + 1))}
      </React.Fragment>
    );
  };

  // Filter programs by search text (keeps parent nodes if any child matches)
  const filterPrograms = (programs: Program[], search: string): Program[] => {
    if (!search) return programs;
    const lowerSearch = search.toLowerCase();
    return programs.reduce<Program[]>((acc, program) => {
      const nameMatches = program.pgName.toLowerCase().includes(lowerSearch);
      const filteredChildren = program.children ? filterPrograms(program.children, search) : [];
      if (nameMatches || filteredChildren.length > 0) {
        acc.push({
          ...program,
          children: nameMatches ? program.children : filteredChildren.length > 0 ? filteredChildren : program.children,
        });
      }
      return acc;
    }, []);
  };

  const filteredPrograms = filterPrograms(state.programs, searchText);

  if (state.loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/user">
          <UserOutlined />
          <span>使用者管理</span>
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/user/personal-permissions">
          個人權限管理
        </Breadcrumb.Item>
        <Breadcrumb.Item>編輯權限</Breadcrumb.Item>
      </Breadcrumb>

      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              編輯個人權限 - {state.user?.userName}
            </Title>
            <Text type="secondary">
              使用者ID: {state.user?.userId} | 部門: {state.user?.department?.dptName || 'N/A'}
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                onClick={handleCancel}
                icon={<RollbackOutlined />}
                size="large"
              >
                返回
              </Button>
              <Button 
                type="primary"
                onClick={handleSave}
                loading={state.saving}
                icon={<SaveOutlined />}
                size="large"
              >
                儲存變更
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        <div style={{ marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', fontSize: 13, color: '#666' }}>
          <span>圖示說明：</span>
          <span><MinusCircleOutlined style={{ color: '#d9d9d9' }} /> 未設定（繼承群組）</span>
          <span><CheckCircleFilled style={{ color: '#52c41a' }} /> 明確允許</span>
          <span><CloseCircleFilled style={{ color: '#ff4d4f' }} /> 明確禁止</span>
          <span style={{ marginLeft: 8, color: '#999' }}>點擊可循環切換</span>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Row align="middle">
            <Col span={8}>
              <Title level={5} style={{ margin: 0 }}>程式名稱</Title>
            </Col>
            <Col span={16}>
              <Title level={5} style={{ margin: 0, textAlign: 'center' }}>權限設定</Title>
            </Col>
          </Row>
        </div>
        
        <Input.Search
          placeholder="搜尋程式名稱..."
          allowClear
          style={{ marginBottom: 12 }}
          onChange={e => setSearchText(e.target.value)}
        />

        <div style={{
          border: '1px solid #f0f0f0',
          borderRadius: '4px',
          padding: '16px',
          backgroundColor: '#fafafa'
        }}>
          {filteredPrograms.length > 0 ? (
            filteredPrograms.map(program => renderProgramPermissions(program))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">沒有可用的程式</Text>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PersonalPermissionEdit;